import { createContext, type FC, type ReactNode, useContext } from 'react';
import { type SupportedCurrencies } from 'dibs-currency-helpers/exports/currencies.ts';

export type ServerVarsContextType = {
    isBot?: boolean; // should be set for any bot
    isSsrOnlyBot?: boolean; // should be set only for SSR-only bot
    isDesktop?: boolean | null;
    isMobile?: boolean | null;
    areDisplayAdsEnabled?: boolean | null;
    isTablet?: boolean | null;
    clientConf?: { [key: string]: string | number | undefined } | null;
    localeOrigin?: string | null;
    isSearchQueryApplied?: boolean | null;
    locale?: string | null;
    originalUrl?: string | null;
    serverCurrency?: SupportedCurrencies;
    isQuickFiltersRedesignVariant?: boolean;
    isDriveSavedSearchAdoptionVariant?: boolean;
    isTradeQuickFiltersVariant?: boolean;
    isPdpMwLayoutShiftFixVariant?: boolean;
    isTableSizeFilterVariant?: boolean;
    urgencySignalsMwVariant?: string;
    isContentVisibilitySbVariant?: boolean;
    isPdpRespPreviewPage?: boolean;
    isShipsFromSBTileVariant?: boolean;
    isRemoveCountsVariant?: boolean;
    isPdpSimilarSoldItemsModuleVariant?: boolean;
    isPdpEstimateSimilarSoldTestVariant?: boolean;
    isItemFavoritingFeb2026Variant?: boolean;
    isItemFavoritingFeb2026Control?: boolean;
    pdpSponsoredMoreSimilarItemsGridTest?: string;
    pdpSponsoredInfiniteGridMwTest?: string;
    isPdpInfiniteScrollSubModuleVariant?: boolean;
    isPdpSellerModuleTestVariant?: boolean;
    soldPdpSponsoredYmalGridTest?: string;
    isPdpItemModuleTestVariant?: boolean;
    isL3CategoryFilterDisplayVariant?: boolean;
};

export const ServerVarsContext = createContext<ServerVarsContextType>({
    isBot: false,
    isSsrOnlyBot: false,
    isDesktop: null,
    isMobile: null,
    areDisplayAdsEnabled: null,
    isTablet: null,
    clientConf: {},
    localeOrigin: null,
    locale: null,
    originalUrl: null,
    isSearchQueryApplied: null,
    serverCurrency: 'USD',
    isQuickFiltersRedesignVariant: false,
    isDriveSavedSearchAdoptionVariant: false,
    isTradeQuickFiltersVariant: false,
    isPdpMwLayoutShiftFixVariant: false,
    isTableSizeFilterVariant: false,
    urgencySignalsMwVariant: undefined, // if test is off, "urgencySignalsMwVariant" should not have value
    isContentVisibilitySbVariant: false,
    isPdpRespPreviewPage: false,
    isShipsFromSBTileVariant: false,
    isRemoveCountsVariant: false,
    isPdpSimilarSoldItemsModuleVariant: false,
    isPdpEstimateSimilarSoldTestVariant: false,
    isItemFavoritingFeb2026Variant: false,
    isItemFavoritingFeb2026Control: false,
    pdpSponsoredMoreSimilarItemsGridTest: undefined,
    pdpSponsoredInfiniteGridMwTest: undefined,
    isPdpInfiniteScrollSubModuleVariant: false,
    isPdpSellerModuleTestVariant: false,
    soldPdpSponsoredYmalGridTest: undefined,
    isPdpItemModuleTestVariant: false,
    isL3CategoryFilterDisplayVariant: false,
});

export const useServerVarsContext = (): ServerVarsContextType =>
    useContext<ServerVarsContextType>(ServerVarsContext);

export const ServerVarsContextConsumer = ServerVarsContext.Consumer;

export const ServerVarsContextProvider: FC<ServerVarsContextType & { children: ReactNode }> = ({
    children,
    isDesktop,
    isMobile,
    areDisplayAdsEnabled,
    isTablet,
    clientConf,
    localeOrigin,
    originalUrl,
    isSearchQueryApplied,
    locale,
    isBot,
    isSsrOnlyBot,
    serverCurrency,
    isQuickFiltersRedesignVariant,
    isDriveSavedSearchAdoptionVariant,
    isTradeQuickFiltersVariant,
    isPdpMwLayoutShiftFixVariant,
    isTableSizeFilterVariant,
    urgencySignalsMwVariant,
    isContentVisibilitySbVariant,
    isPdpRespPreviewPage,
    isShipsFromSBTileVariant,
    isRemoveCountsVariant,
    isPdpSimilarSoldItemsModuleVariant,
    isPdpEstimateSimilarSoldTestVariant,
    isItemFavoritingFeb2026Variant,
    isItemFavoritingFeb2026Control,
    pdpSponsoredMoreSimilarItemsGridTest,
    pdpSponsoredInfiniteGridMwTest,
    isPdpInfiniteScrollSubModuleVariant,
    isPdpSellerModuleTestVariant,
    soldPdpSponsoredYmalGridTest,
    isPdpItemModuleTestVariant,
    isL3CategoryFilterDisplayVariant,
}) => {
    return (
        <ServerVarsContext
            value={{
                isDesktop,
                isMobile,
                areDisplayAdsEnabled,
                isTablet,
                clientConf,
                localeOrigin,
                originalUrl,
                isSearchQueryApplied,
                locale,
                isBot,
                isSsrOnlyBot,
                serverCurrency,
                isQuickFiltersRedesignVariant,
                isDriveSavedSearchAdoptionVariant,
                isTradeQuickFiltersVariant,
                isPdpMwLayoutShiftFixVariant,
                isTableSizeFilterVariant,
                urgencySignalsMwVariant,
                isContentVisibilitySbVariant,
                isPdpRespPreviewPage,
                isShipsFromSBTileVariant,
                isRemoveCountsVariant,
                isPdpSimilarSoldItemsModuleVariant,
                isPdpEstimateSimilarSoldTestVariant,
                isItemFavoritingFeb2026Variant,
                isItemFavoritingFeb2026Control,
                pdpSponsoredMoreSimilarItemsGridTest,
                pdpSponsoredInfiniteGridMwTest,
                isPdpInfiniteScrollSubModuleVariant,
                isPdpSellerModuleTestVariant,
                soldPdpSponsoredYmalGridTest,
                isPdpItemModuleTestVariant,
                isL3CategoryFilterDisplayVariant,
            }}
        >
            {children}
        </ServerVarsContext>
    );
};
